import React from "react"
import PropTypes from "prop-types"

import { debounce } from "lodash"

import { Link } from "gatsby-plugin-react-i18next"

import Modal from "../_base/Modal"

import {
  getActivePageId,
  getMenuItems,
  getPageRootMenuItemId,
  getMidLevelMenuItemId,
  getPageMenuItemId,
  getExcerpt,
  getChildMenuItems,
  getSupportedLanguages,
  getConnectedPageBranding,
} from "./helpers/MenuHelpers"

import CascadingMenu from "../CascadingMenu"
import LanguageSwitcher from "../LanguageSwitcher"
import TopMenu from "../TopMenu"
import TopMenuExtended from "../TopMenuExtended"
import SearchWidget from "../SearchWidget"
import Footer from "../Footer"

import logoCode from "../../images/logo-code.svg"
import logoCubewise from "../../images/logo-cubewise.svg"
import logoEdu from "../../images/logo-cubewise-edu.svg"
import logoSupport from "../../images/logo-cubewise-support.svg"

import * as supportStyles from "../../styles/Support.module.css"

const HOVER_DELAY_IN_MSECS = 300

export default function Support(props) {
  const [extendedMenuItem, setExtendedMenuItem] = React.useState()
  const [mobileHamburgerOpen, setMobileHamburgerOpen] = React.useState(false)
  const [isPageTopOfScreen, setIsPageTopOfScreen] = React.useState(true)

  const canHover = React.useRef(
    typeof window !== "undefined" &&
      !(
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      ),
  )
  const hoverTrigger = React.useRef(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePageScroll = React.useCallback(
    debounce(() => {
      setIsPageTopOfScreen(window.scrollY === 0)
    }, 250),
    [],
  )
  React.useEffect(() => {
    window.addEventListener(`scroll`, handlePageScroll)

    // cleanup this component
    return () => {
      window.removeEventListener(`scroll`, handlePageScroll)
    }
  }, [handlePageScroll])

  const handleMouseEnter = menuId => {
    // console.log(
    //   "handleMouseEnter",
    //   menuId,
    //   extendedMenuItem,
    //   hoverTrigger.current
    // )

    if (hoverTrigger.current) hoverTrigger.current.cancel()

    if (extendedMenuItem && menuId === extendedMenuItem.id) return

    hoverTrigger.current = debounce(() => {
      // console.log("helllooooooo debouncing on handleMouseEnter", menuId)
      handleToggle(menuId)
      hoverTrigger.current = null
    }, HOVER_DELAY_IN_MSECS)
    hoverTrigger.current()
  }

  const handleMouseLeave = menuId => {
    // console.log("handleMouseLeave", menuId, hoverTrigger)

    if (hoverTrigger.current) hoverTrigger.current.cancel()

    hoverTrigger.current = debounce(() => {
      // console.log("helllooooooo debouncing on handleMouseLeave", menuId)
      setExtendedMenuItem(null)
      hoverTrigger.current = null
    }, HOVER_DELAY_IN_MSECS)
    hoverTrigger.current()
  }

  const handleClickOrTap = menuId => {
    handleToggle(menuId)
  }

  const handleMenuSelection = menuId => {
    setMobileHamburgerOpen(false)
  }

  const handleToggle = menuId => {
    if (extendedMenuItem && menuId === extendedMenuItem.id)
      return setExtendedMenuItem(null)

    const menuItems = getMenuItems(props.data.menu)
    const menuItemsWithSameId = menuItems.filter(item => item.id === menuId)
    if (!menuItemsWithSameId.length) return setExtendedMenuItem(null)

    const selectedMenuItem = menuItemsWithSameId[0]

    setExtendedMenuItem(selectedMenuItem)
  }

  return (
    <main className={supportStyles.wrapper}>
      <header
        className={`container-fluid ${
          isPageTopOfScreen ? supportStyles.atTopPage : supportStyles.scrolling
        }`}
      >
        <div className="row g-0">
          <div className={`col-4 col-xl-2 ${supportStyles.logoSection}`}>
            <Link to="/">
              {extendedMenuItem &&
                getConnectedPageBranding(extendedMenuItem) ===
                  `code-branding` && <img src={logoCode} alt="Cubewise Code" />}
              {extendedMenuItem &&
                getConnectedPageBranding(extendedMenuItem) ===
                  `cubewise-branding` && (
                  <img src={logoCubewise} alt="Cubewise" />
                )}
              {extendedMenuItem &&
                getConnectedPageBranding(extendedMenuItem) ===
                  `education-branding` && (
                  <img src={logoEdu} alt="Cubewise EDU" />
                )}
              {(!extendedMenuItem ||
                getConnectedPageBranding(extendedMenuItem) ===
                  `support-branding`) && (
                <img src={logoSupport} alt="Cubewise Support" />
              )}
            </Link>
          </div>
          <div
            className={`d-none d-xl-flex col-xl-7 ${supportStyles.topMenuSection}`}
          >
            <TopMenu
              menuItems={getMenuItems(props.data.menu)}
              rootMenuItemId={getPageRootMenuItemId(
                props.data.wpPage,
                props.data.menu,
              )}
              extendedMenuItemId={extendedMenuItem && extendedMenuItem.id}
              activePageId={getActivePageId(props.data.wpPage)}
              pageContext={props.pageContext}
              canHover={canHover.current}
              onToggle={handleClickOrTap}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          </div>
          <div className={`col col-xl-2 ${supportStyles.actions}`}>
            {extendedMenuItem &&
              getConnectedPageBranding(extendedMenuItem) ===
                `code-branding` && (
                <button className="code-branding">Contact us</button>
              )}
            {extendedMenuItem &&
              getConnectedPageBranding(extendedMenuItem) ===
                `education-branding` && (
                <button className="education-branding">Contact us</button>
              )}
            {extendedMenuItem &&
              getConnectedPageBranding(extendedMenuItem) ===
                `cubewise-branding` && (
                <button className="cubewise-branding">Contact us</button>
              )}
            {(!extendedMenuItem ||
              getConnectedPageBranding(extendedMenuItem) ===
                `support-branding`) && (
              <Modal url="/contact-us" modalsRepo={props.data.modals}>
                {({ handleOpenModal }) => {
                  return (
                    <button
                      className="support-branding"
                      onClick={handleOpenModal}
                    >
                      Contact us
                    </button>
                  )
                }}
              </Modal>
            )}
            <LanguageSwitcher
              canBeOpen={extendedMenuItem === null}
              supportedLanguages={getSupportedLanguages(props.data.languages)}
              pageContext={props.pageContext}
              onToggle={val => val && setExtendedMenuItem(null)}
            />
            {/* <img src={search} alt="global-search" /> */}
          </div>
          {/* <div className={`col-1 ${supportStyles.ctaSection}`}>
          <button>Contact us</button>
        </div> */}
          {/* <div className={`col-2 col-xxl-1 ${supportStyles.languageSwitcher}`}>
          <LanguageSwitcher
            supportedLanguages={getSupportedLanguages(props.data.languages)}
            pageContext={props.pageContext}
          />
        </div> */}
          <div className={`col d-none d-xl-flex ${supportStyles.globalSearch}`}>
            <SearchWidget pageContext={props.pageContext} />
          </div>
          <div className={`col-2 d-flex d-xl-none ${supportStyles.mobileMenu}`}>
            <button
              className="mobile-hamburger"
              onClick={() => setMobileHamburgerOpen(!mobileHamburgerOpen)}
            >
              {!mobileHamburgerOpen && <i className="bi bi-list"></i>}
              {mobileHamburgerOpen && <i className="bi bi-chevron-right"></i>}
            </button>
          </div>
        </div>
        {mobileHamburgerOpen && (
          <div className={`row g-0 ${supportStyles.mobileCascadeMenuHolder}`}>
            <div className={`col-12 ${supportStyles.mobileCascadeMenu}`}>
              <CascadingMenu
                menuItems={getMenuItems(props.data.menu)}
                activePageId={getActivePageId(props.data.wpPage)}
                pageContext={props.pageContext}
                onToggle={handleMenuSelection}
              />
            </div>
          </div>
        )}

        {extendedMenuItem && (
          <div
            className={`row g-0 extended-menu ${supportStyles.extendedMenuSection}`}
            onMouseEnter={() => handleMouseEnter(extendedMenuItem.id)}
            onMouseLeave={() => handleMouseLeave(extendedMenuItem.id)}
            role="presentation"
          >
            <div className="col">
              <TopMenuExtended
                rootMenuId={extendedMenuItem.id}
                rootMenuConnectedId={
                  extendedMenuItem.connectedNode &&
                  extendedMenuItem.connectedNode.node &&
                  extendedMenuItem.connectedNode.node.id
                }
                midLevelMenuId={getMidLevelMenuItemId(
                  props.data.wpPage,
                  props.data.menu,
                )}
                menuId={getPageMenuItemId(props.data.wpPage, props.data.menu)}
                label={extendedMenuItem.label}
                excerpt={getExcerpt(extendedMenuItem)}
                childMenuItems={getChildMenuItems(
                  getMenuItems(props.data.menu),
                  extendedMenuItem,
                )}
                pageContext={props.pageContext}
                className={getConnectedPageBranding(extendedMenuItem)}
                onItemSelected={id => {
                  if (
                    id === getPageMenuItemId(props.data.wpPage, props.data.menu)
                  )
                    setExtendedMenuItem(null)
                }}
              />
            </div>
          </div>
        )}
      </header>

      <section className="container-fluid body">{props.children}</section>

      <footer className="container-fluid">
        <div className="row">
          <div className="col">
            <Footer
              menuItems={getMenuItems(props.data.footerMenu)}
              pageContext={props.pageContext}
              modals={props.data.modals}
            />
          </div>
        </div>
      </footer>
    </main>
  )
}

Support.propTypes = {
  data: PropTypes.shape({
    wpPage: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          content: PropTypes.string,
        }).isRequired,
      ).isRequired,
    }),

    locales: PropTypes.shape({
      edges: PropTypes.array.isRequired,
    }).isRequired,

    languages: PropTypes.shape({
      edges: PropTypes.array,
    }).isRequired,

    menu: PropTypes.shape({
      menuItems: PropTypes.shape({
        nodes: PropTypes.array.isRequired,
      }).isRequired,
    }).isRequired,

    footerMenu: PropTypes.shape({
      menuItems: PropTypes.shape({
        nodes: PropTypes.array.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
}
